import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { PassportCard as Card } from '../../../services/api/passportCards';
import CollectorCard from '../CollectorCard';
import PassportCard from '../PassportCard';
import CollectorCardSkeleton from '../CollectorCardSkeleton';
import { collectionRequest, type CollectionResult, type Group } from './collectionState';
import { timelineCollectedLabel, timelineMonthLabel } from './passportTimelineUtils';
import './passport-timeline.css';

type Props = {
  groups: Group[];
  query: string;
  opened: string[];
  onOpenMonths: (keys: string[]) => void;
  inspect: (card: Card, cards: Card[]) => void;
  refresh: () => void;
};

export default function PassportTimeline({
  groups,
  query,
  opened,
  onOpenMonths,
  inspect,
  refresh,
}: Props) {
  const root = useRef<HTMLDivElement>(null);
  const [jumpKey, setJumpKey] = useState('');
  const [announcement, setAnnouncement] = useState('');
  function jump(key: string) {
    if (!key) return;
    onOpenMonths(Array.from(new Set([...opened, key])));
    setJumpKey(key);
  }
  useEffect(() => {
    if (!jumpKey) return;
    const heading = root.current?.querySelector<HTMLButtonElement>(
      `[data-month-heading="${jumpKey}"]`
    );
    heading?.focus({ preventScroll: true });
    heading?.scrollIntoView({
      block: 'start',
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
    setJumpKey('');
  }, [jumpKey]);
  return (
    <div className="pt-timeline" ref={root}>
      <aside className="pt-month-nav" aria-label="Jump to collected month">
        <span className="bc-caption">The chapters</span>
        <p>Months follow saved collection dates.</p>
        <label className="pt-month-select">
          Jump to month
          <select value="" onChange={(e) => jump(e.target.value)}>
            <option value="">Choose a month</option>
            {groups.map((g) => (
              <option key={g.key} value={g.key}>
                {timelineMonthLabel(g.key)} · {g.count}
              </option>
            ))}
          </select>
        </label>
        <div className="pt-month-links">
          {groups.map((g) => (
            <button key={g.key} onClick={() => jump(g.key)}>
              <span>{timelineMonthLabel(g.key)}</span>
              <span>{g.count}</span>
            </button>
          ))}
        </div>
      </aside>
      <div className="pt-months">
        {groups.map((g, index) => (
          <React.Fragment key={`${query}:${g.key}`}>
            {g.key !== 'unknown' &&
              (index === 0 || groups[index - 1].key.slice(0, 4) !== g.key.slice(0, 4)) && (
                <h3 className="pt-year">{g.key.slice(0, 4)}</h3>
              )}
            <MonthStack
              group={g}
              query={query}
              opened={opened.includes(g.key)}
              inspect={inspect}
              refresh={refresh}
              announce={setAnnouncement}
              toggle={() => {
                const isOpen = opened.includes(g.key);
                onOpenMonths(isOpen ? opened.filter((key) => key !== g.key) : [...opened, g.key]);
                setAnnouncement(
                  `${timelineMonthLabel(g.key)} ${isOpen ? 'collapsed' : 'expanded'}.`
                );
              }}
            />
          </React.Fragment>
        ))}
      </div>
      <span className="bc-sr" role="status">
        {announcement}
      </span>
    </div>
  );
}

function MonthStack({
  group,
  query,
  opened,
  toggle,
  inspect,
  refresh,
  announce,
}: {
  group: Group;
  query: string;
  opened: boolean;
  toggle: () => void;
  inspect: Props['inspect'];
  refresh: () => void;
  announce: (message: string) => void;
}) {
  const root = useRef<HTMLElement>(null);
  const generation = useRef(0);
  const [near, setNear] = useState(false);
  const [covers, setCovers] = useState<Card[]>([]);
  const [data, setData] = useState<CollectionResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [changed, setChanged] = useState(false);
  const label = timelineMonthLabel(group.key);
  const headingId = `passport-month-${group.key}`;
  useEffect(() => {
    if (!root.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: '200px' }
    );
    observer.observe(root.current);
    return () => observer.disconnect();
  }, []);
  const load = useCallback(
    async (preview: boolean, cursor?: string) => {
      const id = ++generation.current;
      setBusy(true);
      if (!preview && !cursor) setData(null);
      setError('');
      setChanged(false);
      try {
        const params = new URLSearchParams(query);
        params.set('groupKey', group.key);
        params.set('limit', preview ? '3' : '6');
        if (cursor) params.set('cursor', cursor);
        const result = await collectionRequest<CollectionResult>(params);
        if (id !== generation.current) return;
        if (preview) setCovers(result.items);
        else {
          setData((old) =>
            cursor && old
              ? {
                  ...result,
                  items: Array.from(
                    new Map(
                      [...old.items, ...result.items].map((card) => [card.key, card])
                    ).values()
                  ),
                }
              : result
          );
          setCovers(result.items.slice(0, 3));
          announce(
            `${label}: ${cursor ? 'more cards loaded' : `${result.items.length} of ${result.groupTotal} cards shown`}.`
          );
        }
      } catch (e) {
        if (id !== generation.current) return;
        setError((e as Error).message);
        const conflict = (e as { code?: string }).code === 'conflict';
        setChanged(conflict);
        if (conflict) {
          setData(null);
          setCovers([]);
        }
      } finally {
        if (id === generation.current) setBusy(false);
      }
    },
    [query, group.key, label, announce]
  );
  useEffect(() => {
    if (opened) void load(false);
    else if (near) void load(true);
    const currentGeneration = generation;
    return () => {
      currentGeneration.current++;
    };
  }, [opened, near, load]);
  const visibleCards = data?.items || [];
  return (
    <section
      className={`pt-month ${opened ? 'is-open' : ''} ${group.key === 'unknown' ? 'pt-undated' : ''}`}
      ref={root}
      aria-labelledby={headingId}
    >
      <div className="pt-month-header">
        <div>
          <span className="pt-rail-dot" aria-hidden="true" />
          <h4>
            <button
              id={headingId}
              data-month-heading={group.key}
              aria-expanded={opened}
              aria-controls={`${headingId}-cards`}
              onClick={toggle}
            >
              {label}
              <span aria-hidden="true">{opened ? '−' : '+'}</span>
            </button>
          </h4>
          <p>
            {group.count} {group.count === 1 ? 'passport' : 'passports'}
            {group.key === 'unknown' && ' · No reliable collection date'}
          </p>
        </div>
        <button
          className="pt-open"
          onClick={toggle}
          aria-expanded={opened}
          aria-controls={`${headingId}-cards`}
        >
          {opened ? 'Gather stack' : 'Open stack'} <span aria-hidden="true">↗</span>
          <span className="bc-sr"> {label}</span>
        </button>
      </div>
      {!opened && (
        <div className="pt-collapsed">
          <div
            className="pt-preview"
            aria-hidden="true"
            ref={(node) => node?.setAttribute('inert', '')}
          >
            {(covers.length ? covers : [null, null, null].slice(0, Math.min(3, group.count))).map(
              (card, index) => (
                <div
                  className="pt-preview-card"
                  key={card?.key || index}
                  style={{ '--pt-card': index } as React.CSSProperties}
                >
                  {card ? (
                    <PassportCard card={card} quiet loadBuildDetails={false} />
                  ) : (
                    <div className="pt-card-placeholder">
                      B:<small>PASSPORT</small>
                    </div>
                  )}
                </div>
              )
            )}
          </div>
          <p>
            {group.key === 'unknown'
              ? 'Still part of your story.'
              : 'A few good finds. One chapter.'}
          </p>
        </div>
      )}
      <div id={`${headingId}-cards`} hidden={!opened}>
        {opened && (
          <>
            {busy && !data && (
              <div className="pt-cards" role="status" aria-busy="true">
                <span className="bc-sr">Opening {label}…</span>
                {[0, 1, 2].map((n) => (
                  <CollectorCardSkeleton key={n} kind="vehicle" />
                ))}
              </div>
            )}
            {error && (
              <p className="pt-error" role="alert">
                {error}{' '}
                <button
                  disabled={busy}
                  onClick={() => (changed ? refresh() : load(false, data?.nextCursor || undefined))}
                >
                  {changed ? 'Refresh timeline' : 'Try again'}
                </button>
              </p>
            )}
            <div className="pt-cards">
              {visibleCards.map((card, index) => (
                <article
                  className="pt-memory"
                  data-binder-key={card.key}
                  key={card.key}
                  style={{ '--pt-tilt': `${((index % 3) - 1) * 1.5}deg` } as React.CSSProperties}
                >
                  <CollectorCard
                    card={card}
                    onOpen={() => inspect(card, visibleCards)}
                    openLabel="Inspect passport"
                  />
                  <p className="pt-date">{timelineCollectedLabel(card.earned_at)}</p>
                </article>
              ))}
            </div>
            {data && (
              <div className="pt-load-row">
                <span>
                  {visibleCards.length} of {data.groupTotal} passports
                </span>
                {data.nextCursor && (
                  <button disabled={busy || !!error} onClick={() => load(false, data.nextCursor!)}>
                    {busy ? 'Opening…' : 'More in this month'}
                  </button>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
